import { ITutorialElements } from '@/models/user-tutorial/IUserTutorial';
import utils from '@/utils/QSUtils';
import PopoverService from '@/modules/popovers/services/popoverService';
import { Actions, store, userSettingsStore } from '@/store';

import GuideManager from '@/manager/GuideManager';
import LogUsageManager from './LogUsageManager';

export default {
    async showTutorial(step: number, hasCompleted: boolean) {
        await utils.sleep(500);
        this.initialize(step, hasCompleted);
    },
    async initialize(step: number, hasCompleted: boolean) {
        this.removeTopOverlay();
        this.removeBottomOverlay();

        this.addTopOverlay();
        this.addBottomOverlay();

        await this.setScrollArea(step);
        this.setTopOverlay(step);
        this.launchPopover(step, hasCompleted);
    },
    addTopOverlay() {
        const element = document.createElement('div') as HTMLDivElement;
        element.setAttribute('id', 'tutorialOverlayTop');
        element.classList.add('tutorial-overlay-top');
        document.body.appendChild(element);
    },
    removeTopOverlay() {
        const element = document.getElementById('tutorialOverlayTop') as HTMLDivElement;
        element?.parentNode?.removeChild(element);
    },
    addBottomOverlay() {
        const element = document.createElement('div') as HTMLElement;
        element.setAttribute('id', 'tutorialOverlayBottom');
        element.classList.add('tutorial-overlay-bottom');
        document.body.appendChild(element);
    },
    removeBottomOverlay() {
        const element = document.getElementById('tutorialOverlayBottom') as HTMLElement;
        element?.parentNode?.removeChild(element);
    },
    setTopOverlay(step: number) {
        const item = this.getTutorialElements(step) as ITutorialElements;
        const offset = utils.getOffset(item.element);
        let width = item.element.offsetWidth;
        let height = item.element.offsetHeight;

        width = width + 20;
        height = height + 20;
        offset.left = offset.left - 10;
        offset.top = offset.top - 10;

        // Adjust height area visible to another elements height
        if (utils.isKeyInObject(item, 'scaleHeightTo') && item.scaleHeightTo !== undefined) {
            height = item.scaleHeightTo.offsetHeight + 10;
        }

        const element = document.getElementById('tutorialOverlayTop') as HTMLDivElement;
        element.style.width = `${width}px`;
        element.style.height = `${height}px`;
        element.style.left = `${offset.left}px`;
        element.style.top = `${offset.top}px`;
    },
    tutorialExit() {
        const grid = document.getElementById('grid') as HTMLElement;
        this.removeTopOverlay();
        this.removeBottomOverlay();
        grid.scrollLeft = 0;
        grid.scrollTop = 0;
    },
    async setScrollArea(step: number) {
        const item = this.getTutorialElements(step) as ITutorialElements;
        const offset = utils.getOffset(item.element);
        if (utils.isKeyInObject(item, 'scrollDirection')) {
            if (item.scrollDirection === 'horizontal' && item.scrollContainer !== undefined) {
                let scrollTo = 0;
                if (utils.isKeyInObject(item, 'scrollOToOverride')) {
                    scrollTo = item.scrollOToOverride || 0;
                } else {
                    scrollTo = offset.left;
                }

                item.scrollContainer.scrollLeft = scrollTo;
            }

            await utils.sleep(500);
        }
    },
    getTutorialElements(step: number) {
        const result = this.tutorialElements();
        return result.find((f: any) => f.step === step) ? result.find((f: any) => f.step === step) : {};
    },
    launchPopover(step: number, hasCompleted: boolean) {
        const item = this.getTutorialElements(step) as ITutorialElements;

        const options = {
            html: true,
            sanitize: false,
            content: this.generateHTML(step),
            placement: 'auto',
            customClass: 'tutorial-popover popover-manual-close-only',
        };

        const tutorialPopover = PopoverService.initialize(item.element, options);
        PopoverService.open(tutorialPopover);
        const el = PopoverService.getPopoverElement(tutorialPopover);

        // On open listeners
        PopoverService.onOpen(tutorialPopover, async () => {
            // Exit button
            if (el.getElementsByClassName('tutorial-exit').length > 0) {
                el.getElementsByClassName('tutorial-exit')[0].addEventListener('click', async () => {
                    this.tutorialExit();
                    PopoverService.hide(tutorialPopover);
                    PopoverService.hideAll();

                    GuideManager.guideCompleted(1);

                    if (!hasCompleted) {
                        LogUsageManager.completedFirstTimeTutorial();

                        await userSettingsStore.setUserTutorialCompleted({
                            userTutorialCompleted: true
                        });
                    }
                });
            }

            // Next button
            if (el.getElementsByClassName('tutorial-next').length > 0) {
                el.getElementsByClassName('tutorial-next')[0].addEventListener('click', () => {
                    PopoverService.hide(tutorialPopover);
                    this.initialize(step + 1, hasCompleted);
                });
            }

            // Previous button
            if (el.getElementsByClassName('tutorial-previous').length > 0) {
                el.getElementsByClassName('tutorial-previous')[0].addEventListener('click', () => {
                    PopoverService.hide(tutorialPopover);
                    this.initialize(step - 1, hasCompleted);
                });
            }

            // Previous button
            if (el.getElementsByClassName('tutorial-remind-me').length > 0) {
                el.getElementsByClassName('tutorial-remind-me')[0].addEventListener('click', () => {
                    this.tutorialExit();
                    PopoverService.hide(tutorialPopover);

                    if (!hasCompleted) {
                        LogUsageManager.remindMeLaterFirstTimeTutorial(step);
                    }
                });
            }
        });
    },
    generateHTML(step: number) {
        const item = this.getTutorialElements(step) as ITutorialElements;
        const totalSteps = this.tutorialElementsCount();
        const btnPrevious = `<div class="tutorial-previous btn tutorial-btn-link">Previous</div>`;
        const btnNext = `<div class="tutorial-next btn btn-outline-secondary">Next</div>`;
        const btnExit = `<div class="tutorial-exit btn tutorial-btn-link">Exit</div>`;
        const btnExitFinish = `<div class="tutorial-exit btn btn-outline-secondary">Exit</div>`;
        let buttons = '';
        if (step === 1) {
            buttons = `${btnNext} ${btnExit}`;
        } else if (step === totalSteps) {
            buttons = `${btnPrevious} ${btnExitFinish}`;
        } else {
            buttons = `${btnPrevious} ${btnNext} ${btnExit}`;
        }

        const html = `<div class="popover-message-container">
                        <div>
                            <div class="d-inline-block img-tutorial ${item.iconCSS}"></div>
                            <div class="d-inline-block align-top tutorial-title">${item.header}</div>
                            <div class="d-inline-block tutorial-step">${step} of ${totalSteps}</div>
                        </div>
                        <div class="tutorial-message">${item.message}</div>
                        <div class="tutorial-footer-container">
                            <div class="row">
                            	<div class="col align-self-center">
                                    <div class="tutorial-remind-me btn tutorial-btn-link">Remind me later</div>
                            	</div>
                            	<div class="col text-end">
                                    ${buttons}
                            	</div>
                            </div>
                        </div>
                    </div>`;

        return html;
    },
    tutorialElementsCount() {
        const result = this.tutorialElements();
        return result.length;
    },
    tutorialElements() {
        const result: ITutorialElements[] = [
            {
                step: 1,
                header: 'Data Sources',
                message: 'Pick and choose which Data Source you want to include in any, or all, fields in your Sales Comparison Grid.',
                element: document.querySelectorAll('.grid-source-column')[0] as HTMLElement,
                iconCSS: 'img-tutorial-database',
                scrollContainer: document.getElementById('grid') as HTMLElement,
                scrollDirection: 'horizontal',
                scrollOToOverride: 0,
            },
            {
                step: 2,
                header: 'Transfer Values',
                message: 'Review your selected data fields, and their sources, that will be sent to your Comps Grid.',
                element: document.getElementById('transferDataHeader') as HTMLElement,
                iconCSS: 'img-tutorial-transfer',
                scaleHeightTo: document.getElementById('grid') as HTMLElement,
            },
            {
                step: 3,
                header: 'Data Preferences',
                message: 'Click to change the default priority for any row in the Data Preference Settings column.',
                element: document.getElementById('preferencesHeader') as HTMLElement,
                iconCSS: 'img-tutorial-priorities',
                scaleHeightTo: document.getElementById('grid') as HTMLElement,
                scrollContainer: document.getElementById('grid') as HTMLElement,
                scrollDirection: 'horizontal',
            },
            {
                step: 4,
                header: '(Only show) Data Discrepancies',
                message: 'Check to view only the variances between each data source.',
                element: document.querySelectorAll('.data-discrepancies-container')[0] as HTMLElement,
                iconCSS: 'img-tutorial-discrepancies',
            },
            {
                step: 5,
                header: 'Data Fact Sheet',
                message: 'Quickly view all the available data and photos for the property in a single document.',
                element: document.getElementById('dataFactSheet') as HTMLElement,
                iconCSS: 'img-tutorial-comps',
            },
            {
                step: 6,
                header: 'Comparable Tabs',
                message: 'Navigate the comparables for this property by clicking each tab. Once a property has been verified within QuickSource, a green checkmark will appear in the tab. Only properties with a green checkmark will be transferred to the report on save.',
                element: document.getElementById('compContainer')?.getElementsByTagName('ul')[0] as HTMLElement,
                iconCSS: 'img-tutorial-search',
            },
            {
                step: 7,
                header: 'Save & Close',
                message: 'On Save, QuickSource will send all verified comps to the report to fill out the Sales Comparison Grid and a consolidated QuickSource data fact sheet within the Workfile.',
                element: document.getElementById('save-report') as HTMLElement,
                iconCSS: 'img-tutorial-save',
            },
        ];
        return result;
    },
};
